import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ApiConfig} from '@employee-configs/api.config';
import * as EmployeeModels from '@employee-models/index';
import {Observable, of} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {
    IBadgeRequestDetailsWithApprovedPhotoResponse,
    IBloodGroupDto,
    IMemberFirmBadgeRequestDetailsResponse,
    INonDeloitteBadgeRequestDetailsResponse,
    IThirdPartyContractorRequestDetailsResponse,
    ITransferBadgeStatusRequest,
    IVisitorResponse,
    IPrehireBadgeRequestDetailsResponse,
    ICleContractorDetails,
    ICleBadgeRequestDetailsResponse,
    IBadgeRequestPhotoInfo,
} from '@employee-models/data.models';
import {BadgeReasonEnum, BadgeRequestCategoryEnum} from '@employee-models/data.enums';
import {UserRuleReader} from '@ui-app/services/user-rules-reader.service';
import {IActionParams, IDeclineActionParams} from '@employee-models/common.models';
import {IReissueValue} from '@employee-pages/badge-requests/request-details/resissue-badge-request/resissue-badge-request.const';
import {EREQUEST_ACTION} from '@employee-pages/badge-requests/request-edit/models';
import { IAccessOnlyCCureResponse } from '@employee-pages/badge-request-page/usi-employee-badge-forms';

@Injectable({
    providedIn: 'root'
})
export class BadgeRequestsService {
    constructor(
        private httpClient: HttpClient,
        private userRuler: UserRuleReader
    ) {
    }


    calculateNumbersOfDays(requestDetails: INonDeloitteBadgeRequestDetailsResponse | IMemberFirmBadgeRequestDetailsResponse): number | string {
        if (!requestDetails.visitStart || !requestDetails.visitEnd) {
            return;
        }

        const startDate = new Date(requestDetails.visitStart);
        const endDate = new Date(requestDetails.visitEnd);


        if (startDate && endDate) {
            const differenceInTime = endDate.getTime() - startDate.getTime();
            return differenceInTime
                ? Math.ceil(differenceInTime / (1000 * 3600 * 24))
                : '-';
        }
    }

    getBadgeRequestsGrid(
        request: EmployeeModels.DM.IBadgeComplianceGridRequest
    ): Observable<EmployeeModels.DM.IBadgeComplianceGridResponse | { error: HttpErrorResponse }> {

        return this.httpClient
            .post<EmployeeModels.DM.IBadgeComplianceGridResponse>(
                ApiConfig.BadgeRequests.getBadgeRequestsGrid,
                request
            )
            .pipe(
                catchError(error => of({error})),
            );
    }

    getMDFBadgeRequestsGrid(
        request: EmployeeModels.DM.IBadgeComplianceGridRequest
    ): Observable<EmployeeModels.DM.IBadgeComplianceGridResponse | { error: HttpErrorResponse }> {

        return this.httpClient
            .post<EmployeeModels.DM.IBadgeComplianceGridResponse>(
                ApiConfig.BadgeRequests.getMDFBadgeRequestsGrid,
                request
            )
            .pipe(
                catchError(error => of({error})),
            );
    }

    getMdfRequestById(requestId: number): Observable<EmployeeModels.DM.IMdfBadgeRequestDetailsResponse> {
        return this.httpClient.get<EmployeeModels.DM.IMdfBadgeRequestDetailsResponse>(
            `${ApiConfig.BadgeRequests.getMdfRequestById}/${requestId}`
        );
    }

    getBloodGroups(): Observable<IBloodGroupDto[]> {
        return this.httpClient.get<IBloodGroupDto[]>(ApiConfig.Dictionary.getBloodGroups);
    }


    getBadgeRequestInfo(headers: { key: string, value: string }[] = []): Observable<EmployeeModels.DM.IBadgeRequestInfoEmployee> {
        // Construct HttpHeaders from the provided headers array
        const httpHeaders = headers.reduce((acc, header) => acc.set(header.key, header.value), new HttpHeaders());

        // Use the constructed headers in the HTTP request options
        const options = { headers: httpHeaders };
        return this.httpClient.get<EmployeeModels.DM.IBadgeRequestInfoEmployee>(ApiConfig.BadgeRequests.getInfo, options);
    }

    getMatchCount(badgeRequestId: number): Observable<number> {
        return this.httpClient.post<number>(ApiConfig.BadgeRequests.getVisitorMatchCount, { badgeRequestId });
    }

    getMatchVisitors(badgeRequestId: number): Observable<IVisitorResponse[]> {
        return this.httpClient.post<IVisitorResponse[]>(ApiConfig.BadgeRequests.getVisitorMatch, { badgeRequestId });
    }

    verifyVisitor(badgeRequestId: number, visitorId: number): Observable<unknown> {
        return this.httpClient.post<number>(ApiConfig.BadgeRequests.verifyVisitor, { badgeRequestId, visitorId });
    }

    // tslint:disable-next-line:max-line-length
    getBadgeRequestById(requestId: number, category: BadgeRequestCategoryEnum, reason?: BadgeReasonEnum): Observable<IBadgeRequestDetailsWithApprovedPhotoResponse | INonDeloitteBadgeRequestDetailsResponse | IThirdPartyContractorRequestDetailsResponse | IMemberFirmBadgeRequestDetailsResponse | ICleBadgeRequestDetailsResponse> {
        switch (category) {
            case BadgeRequestCategoryEnum.ContractorsOrInterns:
            case BadgeRequestCategoryEnum.Employee:
            case BadgeRequestCategoryEnum.FullTimeEmployee:
            case BadgeRequestCategoryEnum.PreHire: {
                switch (reason) {
                    case BadgeReasonEnum.PhotoOnly:
                        return this.httpClient.get<IThirdPartyContractorRequestDetailsResponse>(ApiConfig.BadgeRequests.getPhotoOnlyRequestById + `/${requestId}`);
                        case BadgeReasonEnum.PhotoCompliance:
                            return this.httpClient.get<IThirdPartyContractorRequestDetailsResponse>(ApiConfig.BadgeRequests.getPhotoOnlyRequestById + `/${requestId}`);
                        case BadgeReasonEnum.BadgeLost:
                        return this.httpClient.get<IBadgeRequestDetailsWithApprovedPhotoResponse>
                        (ApiConfig.BadgeRequests.getLostBadgeRequestById + `/${requestId}`);
                    case BadgeReasonEnum.BadgeStolen:
                        return this.httpClient.get<IBadgeRequestDetailsWithApprovedPhotoResponse>
                        (ApiConfig.BadgeRequests.getStolenBadgeRequestById + `/${requestId}`);
                    default:
                        return of(null);
                }
            }
            case BadgeRequestCategoryEnum.ThirdPartyContractor:
                return this.httpClient.get<IThirdPartyContractorRequestDetailsResponse>(ApiConfig.BadgeRequests.getContractIdCard + `/${requestId}`);
            case BadgeRequestCategoryEnum.MemberFirm:
                return this.httpClient.get<IMemberFirmBadgeRequestDetailsResponse>(ApiConfig.BadgeRequests.getMemberVisitorRequest + `/${requestId}`);
            case BadgeRequestCategoryEnum.NonDeloitte:
                return this.httpClient.get<INonDeloitteBadgeRequestDetailsResponse>(ApiConfig.BadgeRequests.getNonDeloitte + `/${requestId}`);
            case BadgeRequestCategoryEnum.CleContractor:
                return this.httpClient.get<ICleBadgeRequestDetailsResponse>(ApiConfig.BadgeRequests.getCle + `/${requestId}`);
            default:
                return of(null);
        }
    }

    saveUpload(request: EmployeeModels.DM.IBadgeRequestModel, headers: { key: string, value: string }[]=[]): Observable<number> {
        // Construct HttpHeaders from the provided headers array
        const httpHeaders = headers.reduce((acc, header) => acc.set(header.key, header.value), new HttpHeaders());
    
        // Use the constructed headers in the HTTP request options
        const options = { headers: httpHeaders };
    
        switch (request.reason) {
            case BadgeReasonEnum.BadgeDamaged:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.badgeDamaged, request, options) as Observable<number>;
            case BadgeReasonEnum.BadgeLost:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.badgeLost, request, options) as Observable<number>;
            case BadgeReasonEnum.BadgeStolen:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.badgeStolen, request, options) as Observable<number>;
            case BadgeReasonEnum.InitialBadgeRequest:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.initialBadgeRequest, request, options) as Observable<number>;
            case BadgeReasonEnum.PhotoOnly:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.photoUpdate, request, options) as Observable<number>;
            case BadgeReasonEnum.PhotoCompliance:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.photoUpdate, request, options) as Observable<number>;
            default:
                return this.httpClient.post(ApiConfig.BadgeRequests.saveUpload, request, options) as Observable<number>;
        }
    }

    saveContractorUpload(request: EmployeeModels.DM.IBadgeRequestModelCle, headers: { key: string, value: string }[]=[]): Observable<number> {
        // Construct HttpHeaders from the provided headers array
        const httpHeaders = headers.reduce((acc, header) => acc.set(header.key, header.value), new HttpHeaders());
    
        // Use the constructed headers in the HTTP request options
        const options = { headers: httpHeaders };
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.cleContractor, request, options) as Observable<number>;
    }
    saveUSIUpload(category: BadgeRequestCategoryEnum, request: EmployeeModels.DM.IBadgeRequestModel, action?: EREQUEST_ACTION, headers: { key: string, value: string }[]=[]): Observable<number> {
        // Construct HttpHeaders from the provided headers array
        const httpHeaders = headers.reduce((acc, header) => acc.set(header.key, header.value), new HttpHeaders());
    
        // Use the constructed headers in the HTTP request options
        const options = { headers: httpHeaders };
        switch (category) {
            case (BadgeRequestCategoryEnum.NonDeloitte):
                switch (action) {
                    case EREQUEST_ACTION.edit:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.nonDeloitteEdit, request) as Observable<number>;
                    case EREQUEST_ACTION.copy:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.nonDeloitteCopy, request) as Observable<number>;
                    default:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.nonDeloitte, request, options) as Observable<number>;
                }

            case (BadgeRequestCategoryEnum.ThirdPartyContractor):
                switch (action) {
                    case EREQUEST_ACTION.edit:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.contractIdEdit, request) as Observable<number>;
                    case EREQUEST_ACTION.copy:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.contractIdCopy, request) as Observable<number>;
                    default:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.contractId, request, options) as Observable<number>;
                }

            case (BadgeRequestCategoryEnum.MemberFirm):
                switch (action){
                    case EREQUEST_ACTION.edit:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.memberVisitorEdit, request) as Observable<number>;
                    case EREQUEST_ACTION.copy:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.memberVisitorCopy, request) as Observable<number>;
                    default:
                        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.memberVisitor, request, options) as Observable<number>;
                }
            default:
                return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.initialBadgeRequest, request, options) as Observable<number>;
        }
    }

    saveMemberVisitorUSI(category: BadgeRequestCategoryEnum, request: EmployeeModels.DM.IBadgeRequestModel): Observable<number[]> {
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.memberVisitor, request) as Observable<number[]>;
    }

    createMdfRequest(request: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.mdfCreateRequest, request) as Observable<any>;
    }

    createIdfRequest(request: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.idfCreateRequest, request) as Observable<any>;
    }

    updateMdfRequest(requestId: number, request: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.mdfEdit, request) as Observable<any>;
    }

    updateIdfRequest(requestId: number, request: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.uploadBadgeRequest.idfEdit, request) as Observable<any>;
    }

    uploadMdfPhoto(badgeRequestId: number, photoInfo: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.mdfPhotoUpload, { badgeRequestId, photoInfo }) as Observable<any>;
    }

    createMobileUploadBadgeRequest(request: { userId: number, reasonId: number }): Observable<{}> {
        return this.httpClient.post(ApiConfig.MobileBadging.saveMobileBadgeRequest, request) as Observable<{}>;
    }

    getMobileBadgeRequestInfo(userId: number): Observable<EmployeeModels.DM.IMobileBadgeRequestInfo> {
        const url = ApiConfig.MobileBadging.getMobileBadgeRequestInfo.replace('{userId}', userId.toString());
        return this.httpClient.get(url) as Observable<EmployeeModels.DM.IMobileBadgeRequestInfo>;
    }

    getDownloadUrl(uId: string): Observable<EmployeeModels.DM.IDownloadUrlResponse> {
        const url = ApiConfig.File.getDownloadUrl.replace('{uId}', uId);
        return this.httpClient.get(url) as Observable<EmployeeModels.DM.IDownloadUrlResponse>;
    }

    getVisitorDownloadUrl(uId: string): Observable<EmployeeModels.DM.IDownloadUrlResponse> {
        const url = ApiConfig.File.getVisitorDownloadUrl.replace('{uId}', uId);
        return this.httpClient.get(url) as Observable<EmployeeModels.DM.IDownloadUrlResponse>;
    }

    getCanUploadBadgeRequest(): Observable<{ [key: string]: boolean }> {
        return this.httpClient.get<{ [key: string]: boolean }>(ApiConfig.BadgeRequests.canUpload)
            .pipe(
                tap(data => this.userRuler.canBadgeRequest = data.result)
            );
    }

    getCanUploadVisitorBadgeRequest(): Observable<{ [key: string]: boolean }> {
        return this.httpClient.get<{ [key: string]: boolean }>(ApiConfig.BadgeRequests.canUploadVisitor)
            .pipe(
                tap(data => this.userRuler.canVisitorsRequest = data.result)
            );
    }

    getCanUploadMobileBadgeRequest(userId: number): Observable<{ [key: string]: boolean }> {
        const url = ApiConfig.MobileBadging.isRequestAvailable.replace('{userId}', userId.toString());
        return this.httpClient.get(url) as Observable<{ [key: string]: boolean }>;
    }

    cancelBadgeRequest(request: IActionParams): Observable<null> {
        const params = new HttpParams({fromObject: JSON.parse(JSON.stringify(request))});

        return this.httpClient.post(ApiConfig.BadgeRequests.cancelBadgeRequest, null, {
            params
        }) as Observable<null>;
    }

    declineBadgeRequest(request: IDeclineActionParams): Observable<null> {
        const params = new HttpParams({fromObject: JSON.parse(JSON.stringify(request))});

        return this.httpClient.post(ApiConfig.BadgeRequests.declineBadgeRequest, null, {
            params
        }) as Observable<null>;
    }

    approveBadgeRequest(request: IActionParams): Observable<null> {
        const params = new HttpParams({fromObject: JSON.parse(JSON.stringify(request))});

        return this.httpClient.post(ApiConfig.BadgeRequests.approveBadgeRequest, null, {
            params
        }) as Observable<null>;
    }

    reissueBadgeRequest(reissueData: IReissueValue): Observable<null> {
        return this.httpClient.post(ApiConfig.BadgeRequests.reissueBadgeRequest, reissueData) as Observable<null>;
    }

    extendBadgeValidity(extensionData: any): Observable<any> {
        return this.httpClient.post(ApiConfig.BadgeRequests.extendBadgeValidity, extensionData);
    }

    changeDeliveryLocation(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        const params = new HttpParams({
            fromObject: request
        });

        return this.httpClient.post(`${ApiConfig.BadgeRequests.changeDeliveryLocation}`, null, {params})
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }
    // TODO temporary solution
    ManualPromoteToCreatedInTransfer(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.ManualPromoteToCreatedInTransfer}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    // TODO temporary solution
    ManualPromoteToPrinting(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.ManualPromoteToPrinting}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }
    /**
     * Badge status actions
     */
    readyToPickUp(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.readyToPickUp}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    provided(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.provided}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    shippedTo(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.shippedTo}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    transferTo(request: ITransferBadgeStatusRequest): Observable<{} | { error: HttpErrorResponse }> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.transferTo}`, request)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

     lostInTransit(request: { userId: string, badgeRequestId: string, lastUpdatedOn: string }): Observable<{} | { error: HttpErrorResponse }> {
         console.log('Payload:', request); // Log the payload
         return this.httpClient.post(`${ApiConfig.BadgeRequests.lostInTransit}`, request)
           .pipe(
             map(() => of({})),
             catchError(error => of({ error }))
           );
       }

    suspendReminders(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        const params = new HttpParams({
            fromObject: request
        });

        return this.httpClient.post(`${ApiConfig.BadgeRequests.suspendReminders}`, null, {params})
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    /**
     * Badge bulk actions
     */
    readyToPickUpBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.readyToPickUpBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    providedBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.providedBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    shippedToBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.shippedToBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }


    /**
     * Photo status actions
     */
    approvePhoto(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        const params = new HttpParams({
            fromObject: request
        });

        return this.httpClient.post(`${ApiConfig.BadgeRequests.approved}`, null, {params})
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    resubmissionPending(payload: EmployeeModels.DM.IRejectedPhotoReasonRequest): Observable<{}> {
        return this.httpClient.post(`${ApiConfig.BadgeRequests.resubmissionPending}`, payload)
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    furtherReviewPhoto(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
        const params = new HttpParams({
            fromObject: request
        });

        return this.httpClient.post(`${ApiConfig.BadgeRequests.furtherReview}`, null, {params})
            .pipe(
                map(() => of({})),
                catchError(error => of({error}))
            );
    }

    /**
     * Photo bulk actions
     */
    approvePhotoBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.approvedBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    resubmissionPendingBulk(payload: EmployeeModels.DM.IResubmissionPendingBulkRequest):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.resubmissionPendingBulk}`, payload)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    cancelPrehireBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
    Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.cancelBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    furtherReviewPhotoBulk(request: EmployeeModels.DM.IChangeStatusBulkModel[]):
        Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }> {

        return this.httpClient.post(`${ApiConfig.BadgeRequests.furtherReviewBulk}`, request)
            .pipe(
                catchError(error => of({error}))
            ) as Observable<EmployeeModels.DM.IBulkChangeStatusResponse & { error: HttpErrorResponse }>;
    }

    exportBadgeRequest(request: EmployeeModels.DM.IGridRequest): Observable<any> {
        
        return this.httpClient.post<any>(ApiConfig.BadgeRequests.exportBadgeRequest, request, {
            // Remove blob response type since API returns JSON
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        }).pipe(
            tap(response => {
               
            }),
            catchError(error => {
                
                throw error;
            })
        );
    }

    exportMDFBadgeRequest(request: EmployeeModels.DM.IGridRequest): Observable<any> {
        return this.httpClient.post<any>(ApiConfig.BadgeRequests.exportMDFBadgeRequest, request, {
            headers: new HttpHeaders({
                'Content-Type': 'application/json'
            })
        });
    }

    getLogsGrid(
        request: EmployeeModels.DM.IAuditLogRangeRequest
    ): Observable<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IAuditLogRangeModel>
        | { error: HttpErrorResponse }> {
        return this.httpClient
            .post<EmployeeModels.DM.ITableWithFilterModelGeneric<EmployeeModels.DM.IAuditLogRangeModel>>(
                ApiConfig.BadgeRequests.getLogsGrid,
                request
            )
            .pipe(
                catchError(error => of({error})),
            );
    }
    //To get Prehire Badge Request Details
    getPrehireBadgeRequestDetails(
        request: number
      ): Observable<IPrehireBadgeRequestDetailsResponse | { error: HttpErrorResponse }> {
        return this.httpClient.get<IPrehireBadgeRequestDetailsResponse>(
            ApiConfig.BadgeRequests.getPrehireBadgeRequestDetails + `/${request}`
          )
          .pipe(
            catchError(error => of({ error }))
          );
      }

    getDeliveryLocationByEmployeeList(userId: string): Observable<EmployeeModels.DM.IDeliveryLocationDto[]> {
        const params = new HttpParams().set('userId', userId);

        return this.httpClient.get(ApiConfig.BadgeRequests.getDeliveryLocationsByEmployee, {params}) as
            Observable<EmployeeModels.DM.IDeliveryLocationDto[]>;
    }

    isLastPhotoApproved(): Observable<any> {
        return this.httpClient.get(ApiConfig.BadgeRequests.isLastPhotoApproved);
    }

    getIsVisitorRequest(requestCategory: number): boolean {
        if (!requestCategory) {
            return false;
        }
        const isEmployee = requestCategory === BadgeRequestCategoryEnum.Employee;
        const isPrehire = requestCategory === BadgeRequestCategoryEnum.PreHire;
        const isFTE = requestCategory === BadgeRequestCategoryEnum.FullTimeEmployee;
        const isCTH = requestCategory === BadgeRequestCategoryEnum.ContractorsOrInterns;
        const isCLE = requestCategory === BadgeRequestCategoryEnum.CleContractor;
        return !isEmployee && !isPrehire && !isFTE && !isCTH && !isCLE;
    }

    cancelPrehireBadgeRequest(request: Record<string, string>): Observable<{} | { error: HttpErrorResponse }> {
            const params = new HttpParams({
                fromObject: request
            });

            return this.httpClient.post(`${ApiConfig.BadgeRequests.cancel}`, null, {params})
                .pipe(
                    map(() => of({})),
                    catchError(error => of({error}))
                );

    }

    getDetailsByBadgeNumber(badgeNumber: string | number): Observable<IAccessOnlyCCureResponse> {
        return this.httpClient.get<IAccessOnlyCCureResponse>(`${ApiConfig.BadgeRequests.getDetailsByBadgeNo}/${badgeNumber}`);
    }

    getWorkPermitApprovers(): Observable<EmployeeModels.DM.IWorkPermitApprover[]> {
        return this.httpClient.get<EmployeeModels.DM.IWorkPermitApprover[]>(ApiConfig.UserManagement.getWorkPermitApprovers);
    }

    searchCleContractors(searchCriteria: string): Observable<ICleContractorDetails[]> {
        return this.httpClient.get<ICleContractorDetails[]>(`${ApiConfig.CleContractorSearch.searchCleContractors}`,
            { params: { searchCriteria } }).pipe(
            map((contractors: ICleContractorDetails[]) => {
                // Add the search criteria as the first option if it doesn't exist in the list
                const exactMatch = contractors.find(contractor => contractor.personId === searchCriteria);
                if (!exactMatch && searchCriteria.trim()) {
                    const customOption: ICleContractorDetails = {
                        personId: searchCriteria,
                        contractorName: ' ',
                        vendorName: '',
                        contractorId: null,
                        contractorDetailsId: null,
                        startDate: null,
                        endDate: null,
                        baseLocation: '',
                        createdDate: null,
                        modifiedDate: null,
                        photoGuid: null,
                        badgeType: 'New'
                    };
                    return [customOption, ...contractors];
                }
                return contractors;
            })
        );
    }



}
